import { Component, ElementRef, computed, input, output, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UiEmptyImageComponent } from './ui-empty-image.component';

export interface PhotoSlot {
    previewUrl: string;
    imagenUuid: string | null;
    file: File | null;
}

@Component({
    selector: 'app-photo-gallery-editor',
    standalone: true,
    imports: [CommonModule, UiEmptyImageComponent],
    template: `
    <div>
      <div class="flex items-center justify-between mb-3">
        <div>
          <p class="text-sm font-bold text-gray-800">{{ title() }}</p>
          <p class="text-xs text-gray-400 mt-0.5">{{ hint() }}</p>
        </div>
        <span *ngIf="photos().length" class="text-xs font-semibold text-gray-400">{{ photos().length }} {{ photos().length === 1 ? 'foto' : 'fotos' }}</span>
      </div>

      <div class="relative aspect-square w-full rounded-[1.75rem] overflow-hidden bg-[#fff5f7] border border-rose-100 shadow-sm">
        <img *ngIf="selectedUrl()" [src]="selectedUrl()" alt="Foto" class="w-full h-full object-cover" />
        <div *ngIf="!selectedUrl()" class="w-full h-full flex flex-col items-center justify-center text-rose-300 px-6 text-center">
          <app-ui-empty-image class="w-16 h-16" iconClass="w-10 h-10"></app-ui-empty-image>
          <p class="text-sm font-semibold mt-2">Todavía no hay fotos</p>
          <p class="text-xs mt-1 text-rose-200">Se ven aquí y se suben al guardar</p>
        </div>
        <span *ngIf="selectedUrl() && selectedIndex() === 0" class="absolute left-3 top-3 text-[10px] font-bold uppercase tracking-wider bg-white/90 text-rose-500 px-2.5 py-1 rounded-full">Principal</span>
      </div>

      <div class="flex gap-3 mt-3 overflow-x-auto pb-1">
        <button
          *ngFor="let photo of photos(); let i = index"
          type="button"
          (click)="select(i)"
          class="relative w-20 h-20 rounded-2xl overflow-hidden border-2 flex-shrink-0 bg-[#fff5f7] transition-all"
          [class.border-rose-500]="i === selectedIndex()"
          [class.border-transparent]="i !== selectedIndex()">
          <img [src]="photo.previewUrl" alt="" class="w-full h-full object-cover" />
          <span *ngIf="photo.file" class="absolute bottom-1 left-1 text-[9px] font-bold bg-white/90 text-rose-500 px-1.5 py-0.5 rounded-full">Nueva</span>
          <button type="button" (click)="remove(i, $event)" class="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-xs hover:bg-black">×</button>
        </button>

        <button
          *ngIf="canAdd()"
          type="button"
          (click)="fileInput()?.nativeElement.click()"
          (dragover)="onDragOver($event)"
          (dragleave)="dragging.set(false)"
          (drop)="onDrop($event)"
          class="w-20 h-20 rounded-2xl flex-shrink-0 border-2 border-dashed flex flex-col items-center justify-center text-rose-400 hover:border-rose-400 hover:bg-rose-50 transition-colors"
          [class.border-rose-400]="dragging()"
          [class.bg-rose-50]="dragging()"
          [class.border-rose-200]="!dragging()">
          <span class="text-2xl leading-none">+</span>
          <span class="text-[10px] font-bold mt-0.5">Añadir</span>
        </button>
      </div>

      <input #fileInputEl type="file" class="hidden" [multiple]="multiple()" accept="image/jpeg,image/png,image/webp" (change)="onFiles($event)" />
      <p *ngIf="error()" class="mt-2 text-xs font-semibold text-rose-500">{{ error() }}</p>
    </div>
    `
})
export class PhotoGalleryEditorComponent {
    title = input('Fotos');
    hint = input('Se muestran aquí y se suben al guardar.');
    multiple = input(true);
    photos = input<PhotoSlot[]>([]);
    photosChange = output<PhotoSlot[]>();

    fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInputEl');
    selectedIndex = signal(0);
    dragging = signal(false);
    error = signal('');

    selectedUrl = computed(() => {
        const list = this.photos();
        if (!list.length) return '';
        const i = Math.min(this.selectedIndex(), list.length - 1);
        return list[i]?.previewUrl || '';
    });

    canAdd = computed(() => this.multiple() || this.photos().length === 0);

    select(index: number) {
        this.selectedIndex.set(index);
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        this.dragging.set(true);
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        this.dragging.set(false);
        const files = event.dataTransfer?.files;
        if (files?.length) {
            this.addFiles(Array.from(files));
        }
    }

    onFiles(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files?.length) {
            this.addFiles(Array.from(input.files));
        }
        input.value = '';
    }

    remove(index: number, event: Event) {
        event.stopPropagation();
        const current = this.photos()[index];
        if (current?.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(current.previewUrl);
        }
        const next = this.photos().filter((_, i) => i !== index);
        this.photosChange.emit(next);
        if (this.selectedIndex() >= next.length) {
            this.selectedIndex.set(Math.max(0, next.length - 1));
        }
    }

    private addFiles(files: File[]) {
        this.error.set('');
        const accepted = files.filter((file) => ['image/jpeg', 'image/png', 'image/webp'].includes(file.type));
        if (accepted.length !== files.length) {
            this.error.set('Solo JPEG, PNG o WebP.');
        }
        if (!accepted.length) {
            return;
        }

        const slots: PhotoSlot[] = accepted.map((file) => ({
            previewUrl: URL.createObjectURL(file),
            imagenUuid: null,
            file
        }));

        if (!this.multiple()) {
            this.photos().forEach((photo) => {
                if (photo.previewUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(photo.previewUrl);
                }
            });
            this.photosChange.emit([slots[0]]);
            this.selectedIndex.set(0);
            return;
        }

        const start = this.photos().length;
        this.photosChange.emit([...this.photos(), ...slots]);
        this.selectedIndex.set(start);
    }
}
